import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Icon,
  Heading,
  SSOButton,
  Text,
} from '@redriverpress/system'

import Loc from '../common/components/Loc'
import { SOCIAL_SSO, STD_ONBOARDING_PATH } from '../common/constants'
import {
  setClassCode,
  setSocialSso,
  setYumiClass,
  useStudentOnboardingContext,
  setCurrentStep,
} from '../context/onboardingStudentContext'
import BottomLink from '../common/components/BottomLink'

export default function Credentials() {
  const history = useHistory()
  const { pathname: routePathname } = useLocation()

  const [
    { classCode, cleverSsoUrl, googleSsoUrl, isInvitation, locale },
    dispatch,
  ] = useStudentOnboardingContext()

  const goToNextPage = () => {
    dispatch(setCurrentStep(1))
    dispatch(setSocialSso(null))
    if (!classCode) {
      history.push({
        pathname: `${STD_ONBOARDING_PATH.signup}/enter-class-code`,
      })
    }

    if (classCode) {
      history.push({
        pathname: `${STD_ONBOARDING_PATH.signup}/what-is-your-name`,
      })
    }
  }

  const handleSsoClick = social => {
    dispatch(setCurrentStep(1))
    social === 'google'
      ? dispatch(setSocialSso(SOCIAL_SSO.google))
      : dispatch(setSocialSso(SOCIAL_SSO.clever))

    if (classCode) {
      history.push({
        pathname: `${STD_ONBOARDING_PATH.signup}/terms-and-conditions`,
      })
    } else {
      history.push({
        pathname: `${STD_ONBOARDING_PATH.signup}/enter-class-code`,
      })
    }
  }

  const getSignupHeading = () => {
    if (googleSsoUrl.includes('redirect_to_sign_up')) {
      return 'We could not find your Google account. Sign up for Ellii'
    }
    if (cleverSsoUrl.includes('redirect_to_sign_up')) {
      return 'We could not find your Clever account. Sign up for Ellii'
    }

    return 'Create an Ellii student account'
  }

  const SignupHeading = getSignupHeading()

  useEffect(() => {
    dispatch(setSocialSso(null))
    if (!isInvitation) {
      dispatch(setClassCode(null))
      dispatch(setYumiClass(null))
    }
  }, [routePathname])

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      minHeight='90vh'
      textAlign='center'
    >
      <Heading
        as='h1'
        styledAs='h2'
        mb={locale ? 0 : [3, null, 4]}
        mt={[4, null, null, 5]}
      >
        {SignupHeading}
      </Heading>
      {!!locale && (
        <Loc
          fontSize={1}
          color='gray700'
          fontWeight='600'
          copy={SignupHeading}
          mb={[3, null, 4]}
        />
      )}

      <Box
        mt={[2, null, 3, 4]}
        display={['block', null, null, 'flex']}
        flexDirection={['unset', null, null, 'row']}
        width={[1, null, '70%', '50%']}
      >
        {/* Google */}
        <Box
          display='flex'
          flexDirection='column'
          flex={1}
          mr={[0, null, null, 1]}
          mb={[2, null, null, 0]}
        >
          <SSOButton
            as='button'
            type='Google'
            url={`${googleSsoUrl}&origin=sign_up`}
            onClick={() => handleSsoClick('google')}
            style={{ alignItems: 'stretch' }}
          >
            Sign up with Google
          </SSOButton>
          {!!locale && (
            <Loc
              fontSize={0}
              color='gray700'
              fontWeight='400'
              copy='Sign up with Google'
            />
          )}
        </Box>

        {/* Clever */}
        <Box
          display='flex'
          flexDirection='column'
          flex={1}
          ml={[0, null, null, 1]}
        >
          <SSOButton
            as='button'
            type='Clever'
            url={`${cleverSsoUrl}&origin=sign_up`}
            onClick={() => handleSsoClick('clever')}
            style={{ alignItems: 'stretch' }}
          >
            Sign up with Clever
          </SSOButton>
          {!!locale && (
            <Loc
              fontSize={0}
              color='gray700'
              fontWeight='400'
              copy='Sign up with Clever'
            />
          )}
        </Box>
      </Box>

      {/* ------------- Divider ------------- */}
      <Box
        my={3}
        display='flex'
        alignItems='center'
        width={[1, null, '70%', '50%']}
      >
        <Box height='2px' bg='gray300' flex='1' />
        <Text px={2} fontWeight='600'>
          or
        </Text>
        <Box height='2px' bg='gray300' flex='1' />
      </Box>

      {/* ------------- Create account / Login form ------------- */}

      <>
        <Box mb={1} width={[1, null, '70%', '50%']}>
          <Button onClick={goToNextPage} width={1} secondary>
            Create account
            <Icon ml={2} icon='arrow-right' />
          </Button>
        </Box>
        {!!locale && (
          <Loc
            fontSize={[0, null, 1]}
            color='gray700'
            fontWeight={['400', null, '600']}
            copy='Create account'
          />
        )}
      </>

      <BottomLink />
    </Box>
  )
}
